import { Link, useNavigate } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import { IconButton } from "components/button";

function BackButton({ backTo, backLabel }) {
  const navigate = useNavigate();
  const shared = {
    icon: <MdArrowBack className="h-5 w-5" />,
    label: backLabel,
    variant: "secondary",
    size: "md",
  };

  if (typeof backTo === "number") {
    return <IconButton {...shared} onClick={() => navigate(backTo)} />;
  }
  return <IconButton {...shared} as={Link} to={backTo} />;
}

/**
 * Page title row: the page's only `<h1>`, optional back button, description,
 * actions and a tab row. Render it as the first child of `PageContainer`.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title Page title (h1, `.text-title`).
 * @param {React.ReactNode} [props.description] One-line supporting copy under the title.
 * @param {string|number} [props.backTo] Back destination: a path (rendered as a Link) or a
 *   history delta such as `-1` (uses `navigate`). Omit for top-level pages.
 * @param {string} [props.backLabel="Back"] Accessible name of the back button, e.g. "Back to Groups".
 * @param {React.ReactNode} [props.leading] Media before the title block (e.g. an `Avatar`); keep it decorative
 *   or give it its own accessible name, since the h1 already names the page.
 * @param {React.ReactNode} [props.meta] Inline content beside the title (status Badge).
 * @param {React.ReactNode} [props.actions] Page actions; at most one primary `Button`.
 *   Right-aligned on md+, wraps below the title on mobile.
 * @param {React.ReactNode} [props.tabs] Tab row (e.g. `Tabs`) rendered below the header.
 * @param {string} [props.className]
 */
export default function PageHeader({
  title,
  description,
  backTo,
  backLabel = "Back",
  leading,
  meta,
  actions,
  tabs,
  className = "",
}) {
  const hasBack = backTo !== undefined && backTo !== null;

  return (
    <header className={className || undefined}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {hasBack && <BackButton backTo={backTo} backLabel={backLabel} />}
          {leading && <div className="shrink-0">{leading}</div>}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-title break-words">{title}</h1>
              {meta}
            </div>
            {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">
            {actions}
          </div>
        )}
      </div>
      {tabs && <div className="mt-4">{tabs}</div>}
    </header>
  );
}
